import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import { teamAPI } from '../services/api';
import './Team.css';

const defaultMembers = [
  {
    id: 'default-1',
    name: 'Veloura Leadership',
    role: 'Executive Direction',
    department: 'Executive Team',
    label: 'Veloura',
    image_url: '',
    slug: 'veloura-leadership',
    bio: 'The people guiding Veloura with a focus on detail, taste, and creative direction.',
    active: true
  }
];

const getDisplayOrder = (member) => {
  const value = Number(member.display_order);
  return Number.isFinite(value) ? value : 0;
};

const getDepartmentOrder = (member) => {
  const value = Number(member.department_order);
  return Number.isFinite(value) ? value : getDisplayOrder(member);
};

const buildMenuItems = () => [
  { type: 'link', label: 'Veloura', to: '/' },
  { type: 'link', label: 'Studios', to: '/studios' },
  { type: 'link', label: 'Team', to: '/team', active: true },
  { type: 'link', label: 'Careers', to: '/careers' },
  { type: 'link', label: 'Partners', to: '/partners' },
  { type: 'link', label: 'News', to: '/news' },
  { type: 'link', label: 'Shop', to: '/shop' },
  { type: 'link', label: 'Account', to: '/account' }
];

const TeamPortrait = ({ member }) => (
  <div className="team-card-image">
    {member.image_url ? (
      <img src={member.image_url} alt={member.name} loading="lazy" />
    ) : (
      <div className="team-card-placeholder">
        <span>{member.name?.charAt(0) || 'V'}</span>
      </div>
    )}
  </div>
);

const TeamCard = ({ member }) => (
  <Link className="team-card" to={`/team/${member.slug || member.id}`}>
    <TeamPortrait member={member} />
    <div className="team-card-copy">
      <h3>{member.name}</h3>
      <p className="team-card-role">{member.role}</p>
      {member.label && <p className="team-card-label">{member.label}</p>}
    </div>
  </Link>
);

const TeamProfile = ({ members, loading }) => {
  const { memberSlug } = useParams();
  const member = members.find((item) => String(item.slug || item.id) === String(memberSlug));

  if (loading) {
    return (
      <section className="team-state-section">
        <p>Loading profile...</p>
      </section>
    );
  }

  if (!member) {
    return (
      <section className="team-state-section">
        <h1>Profile unavailable</h1>
        <p>This team profile is not currently published.</p>
        <Link to="/team" className="team-return-link">Return to Our Team</Link>
      </section>
    );
  }

  return (
    <section className="team-profile-section">
      <div className="team-profile-media">
        <TeamPortrait member={member} />
      </div>
      <div className="team-profile-copy">
        <p className="team-profile-department">{member.department}</p>
        <h1>{member.name}</h1>
        <p className="team-profile-role">{member.role}</p>
        {member.label && <p className="team-profile-label">{member.label}</p>}
        {member.bio && <p className="team-profile-bio">{member.bio}</p>}
        {member.profile_url && (
          <a className="team-return-link" href={member.profile_url} target="_blank" rel="noreferrer">
            View Profile
          </a>
        )}
        <Link to="/team" className="team-return-link secondary">Back to Our Team</Link>
      </div>
    </section>
  );
};

const Team = () => {
  const { memberSlug } = useParams();
  const [members, setMembers] = useState(defaultMembers);
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const loadMembers = async () => {
      try {
        const response = await teamAPI.getPublic();
        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          setMembers(response.data);
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error('Error loading team:', error);
        setMembers(defaultMembers);
      } finally {
        setLoading(false);
      }
    };

    loadMembers();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const groupedMembers = useMemo(() => {
    const groups = members.reduce((departmentGroups, member) => {
      const department = member.department || 'Creative Team';
      const departmentKey = member.department_id ? `department-${member.department_id}` : department;
      const departmentOrder = getDepartmentOrder(member);

      if (!departmentGroups[departmentKey]) {
        departmentGroups[departmentKey] = {
          name: department,
          order: departmentOrder,
          members: []
        };
      }

      departmentGroups[departmentKey].order = Math.min(departmentGroups[departmentKey].order, departmentOrder);
      departmentGroups[departmentKey].members.push(member);
      return departmentGroups;
    }, {});

    return Object.values(groups)
      .map((group) => ({
        ...group,
        members: [...group.members].sort((a, b) => {
          const orderDifference = getDisplayOrder(a) - getDisplayOrder(b);
          return orderDifference || String(a.name || '').localeCompare(String(b.name || ''));
        })
      }))
      .sort((a, b) => {
        const orderDifference = a.order - b.order;
        return orderDifference || a.name.localeCompare(b.name);
      });
  }, [members]);

  return (
    <div className="team-page-wrapper">
      <ResponsiveNavbar
        menuItems={buildMenuItems()}
        logo="VELOURA"
        logoLink="/"
        scrolled={scrolled}
      />

      <main className="team-page">
        {!memberSlug && (
          <section className="team-hero">
            <h1>Our Team</h1>
            <p>The People of Veloura</p>
          </section>
        )}

        {memberSlug ? (
          <TeamProfile members={members} loading={loading} />
        ) : loading ? (
          <section className="team-state-section">
            <p>Loading the people of Veloura...</p>
          </section>
        ) : groupedMembers.length === 0 ? (
          <section className="team-state-section">
            <h2>Team coming soon</h2>
            <p>The Veloura roster is being curated.</p>
          </section>
        ) : (
          <section className="team-groups">
            {groupedMembers.map((group, index) => (
              <div className="team-group" key={group.name}>
                {index > 0 && <div className="team-department-divider" aria-hidden="true" />}
                <div className="team-group-heading">
                  <h2>{group.name}</h2>
                </div>
                <div className="team-grid">
                  {group.members.map((member) => (
                    <TeamCard member={member} key={member.id} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Team;
